import { useCallback, useEffect, useState } from "react";
import { LogOut, RefreshCw, Inbox, Download } from "lucide-react";
import { LoginForm } from "./admin/LoginForm";
import { EnquiryCard } from "./admin/EnquiryCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LAST_VISIT_KEY = "kr_admin_last_visit";
const STATUSES = ["new", "contacted", "closed"];

function detailText(d) {
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e.msg || "").join(" ");
  return "Something went wrong";
}

function buildCSV(enquiries) {
  const cols = ["name", "email", "phone", "company", "project_type", "budget", "status", "note", "message", "created_at"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const rows = [
    cols.join(","),
    ...enquiries.map((q) => cols.map((c) => esc(c === "status" ? q.status || "new" : q[c])).join(",")),
  ];
  return "\ufeff" + rows.join("\r\n");
}

export default function Admin() {
  // Session lives in a secure httpOnly cookie; no token in JS or browser storage.
  const [authed, setAuthed] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [enquiries, setEnquiries] = useState(null);
  const [filter, setFilter] = useState("all");
  const [noteDrafts, setNoteDrafts] = useState({});
  const [savingNote, setSavingNote] = useState("");
  const [lastVisit] = useState(() => localStorage.getItem(LAST_VISIT_KEY));

  const logout = useCallback(async () => {
    try {
      await fetch(`${API}/admin/logout`, { method: "POST", credentials: "include" });
    } catch {
      // cookie will expire on its own; still reset the UI
    }
    setAuthed(false);
    setEnquiries(null);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/enquiries`, { credentials: "include" });
      if (res.status === 401) {
        setAuthed(false);
        setEnquiries(null);
        return;
      }
      if (!res.ok) throw new Error();
      setEnquiries(await res.json());
      localStorage.setItem(LAST_VISIT_KEY, new Date().toISOString());
    } catch {
      setError("Could not load enquiries. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch(`${API}/admin/me`, { credentials: "include" })
      .then((res) => setAuthed(res.ok))
      .catch(() => setAuthed(false));
  }, []);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  const login = async (password) => {
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(detailText(data.detail) || "Login failed");
      setAuthed(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const setStatus = async (id, status) => {
    const prevStatus = enquiries?.find((q) => q.id === id)?.status || "new";
    setEnquiries((prev) => prev.map((q) => (q.id === id ? { ...q, status } : q)));
    try {
      const res = await fetch(`${API}/enquiries/${id}/status`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (res.status === 401) return logout();
      if (!res.ok) throw new Error();
      setError("");
    } catch {
      setEnquiries((prev) => prev.map((q) => (q.id === id ? { ...q, status: prevStatus } : q)));
      setError("Could not update status. Please try again.");
    }
  };

  const onDraftChange = (id, value) => setNoteDrafts((d) => ({ ...d, [id]: value }));

  const saveNote = async (id) => {
    const note = noteDrafts[id] ?? "";
    setSavingNote(id);
    try {
      const res = await fetch(`${API}/enquiries/${id}/note`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note }),
      });
      if (res.status === 401) return logout();
      if (!res.ok) throw new Error();
      setError("");
      setEnquiries((prev) => prev.map((q) => (q.id === id ? { ...q, note } : q)));
      setNoteDrafts((d) => {
        const { [id]: _, ...rest } = d;
        return rest;
      });
    } catch {
      setError("Could not save note. Please try again.");
    } finally {
      setSavingNote("");
    }
  };

  const exportCSV = () => {
    if (!enquiries?.length) return;
    const blob = new Blob([buildCSV(enquiries)], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `kr-studio-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const isNew = (q) => Boolean(lastVisit) && new Date(q.created_at) > new Date(lastVisit);
  const newCount = enquiries ? enquiries.filter(isNew).length : 0;
  const countOf = (s) => (enquiries ? enquiries.filter((q) => (q.status || "new") === s).length : 0);
  const filtered = enquiries ? enquiries.filter((q) => filter === "all" || (q.status || "new") === filter) : null;

  if (authed === null) {
    return (
      <div className="admin-page" data-testid="admin-checking">
        <p className="admin-empty">Checking session…</p>
      </div>
    );
  }

  if (!authed) {
    return <LoginForm onLogin={login} error={error} loading={loading} />;
  }

  return (
    <div className="admin-page" data-testid="admin-dashboard">
      <div className="admin-top">
        <img src="/assets/brand/logo.png" alt="KR Studio" className="admin-logo" />
        <div className="admin-top-actions">
          <button className="btn btn-outline-dark btn-sm" onClick={exportCSV} disabled={!enquiries?.length} data-testid="admin-export-btn">
            <Download size={14} /> Export CSV
          </button>
          <button className="btn btn-outline-dark btn-sm" onClick={load} disabled={loading} data-testid="admin-refresh-btn">
            <RefreshCw size={14} className={loading ? "spin" : ""} /> Refresh
          </button>
          <button className="btn btn-outline-dark btn-sm" onClick={logout} data-testid="admin-logout-btn">
            <LogOut size={14} /> Log out
          </button>
        </div>
      </div>

      <h1 className="admin-title" data-testid="admin-title">
        <Inbox size={28} /> Enquiries {enquiries && <span className="admin-count">{enquiries.length}</span>}
        {newCount > 0 && (
          <span className="admin-new-badge" data-testid="admin-new-badge">
            {newCount} new since last visit
          </span>
        )}
      </h1>

      {error && <div className="form-error" data-testid="admin-load-error">{error}</div>}

      {enquiries && (
        <div className="admin-filter-tabs" data-testid="admin-filter-tabs">
          {["all", ...STATUSES].map((f) => (
            <button
              key={f}
              className={`filter-tab${filter === f ? " active" : ""}`}
              onClick={() => setFilter(f)}
              data-testid={`filter-tab-${f}`}
            >
              {f} <span>{f === "all" ? enquiries.length : countOf(f)}</span>
            </button>
          ))}
        </div>
      )}

      {filtered && filtered.length === 0 && (
        <p className="admin-empty" data-testid="admin-empty">
          {filter === "all" ? "No enquiries yet — new submissions will appear here." : `No ${filter} enquiries.`}
        </p>
      )}

      <div className="enquiry-list" data-testid="enquiry-list">
        {filtered?.map((q) => (
          <EnquiryCard
            key={q.id}
            q={q}
            isNew={isNew(q)}
            noteDraft={noteDrafts[q.id]}
            saving={savingNote === q.id}
            onSetStatus={setStatus}
            onDraftChange={onDraftChange}
            onSaveNote={saveNote}
          />
        ))}
      </div>
    </div>
  );
}
